import { API_BASE_URL } from '@synergy/config';
import { FilterParams } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { createAction } from '@reduxjs/toolkit';

import { AppDispatch, AppState } from '../../store/store';
import { ArticleDto } from '../models/dto/article-dto';
import { ArticleListItemDto } from '../models/dto/article-list-item-dto';
import { ArticlesApi } from '../api/articles-api';
import { PoolSnackbar } from '../components/snackbars/pool-snackbar';
import { SearchOrigin } from '../models/search-origin';
import { SearchResultCounts } from '../models/search-result-counts';
import { enqueueSnackbar } from './snackbar-actions';
import { hideLoadingSpinner, notFoundError, showLoadingSpinner } from './feedback-actions';

const api = new ArticlesApi(API_BASE_URL);

export const articleSpecialOffersReceived = createAction<PaginatedResponse<ArticleListItemDto>>(
    'articles/specialOffers/loaded'
);
export const articleDetailsLoaded = createAction<ArticleDto>('articles/detail/loaded');
export const articleDetailsReset = createAction('articles/detail/reset');

export const articleSearchTermChange = createAction<string>('articles/searchTerm/change');
export const initialArticleSearchResultReceived = createAction<
    PaginatedResponse<ArticleListItemDto, SearchResultCounts>
>('articles/initialSearchResult/loaded');
export const articleSearchResultReceived =
    createAction<PaginatedResponse<ArticleListItemDto, SearchResultCounts>>('articles/searchResult/loaded');
export const articleSearchResultReset = createAction('articles/searchResult/reset');

export const fetchArticleSpecialOffers = (searchParams?: URLSearchParams) => async (dispatch: AppDispatch) => {
    const response = await api.getArticleOffers(searchParams);
    if (response.success) {
        dispatch(articleSpecialOffersReceived(response.result));
    }
    // We don't show an error if failed - just don't display the offers
};

export const fetchArticleSearchResult =
    (searchParams: URLSearchParams, isSearchHistory?: boolean, origin?: SearchOrigin) =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
        const response = await api.getArticlesSearch(searchParams, isSearchHistory, origin);
        if (response.success) {
            dispatch(articleSearchResultReceived(response.result));
            if (searchParams.get(FilterParams.Search) !== getState().articles.articleSearchTerm) {
                dispatch(articleSearchTermChange);
            }
        } else {
            dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
        }
    };

export const fetchInitialArticleSearchResult = (searchTerm: string) => async (dispatch: AppDispatch) => {
    dispatch(showLoadingSpinner());

    // The search page asks this a second time without filters, to know the totals
    // behind its filter chips. Nobody searched for it, so it must not count as one.
    const response = await api.getArticlesSearch(
        new URLSearchParams({ [FilterParams.Search]: searchTerm }),
        undefined,
        'background'
    );
    dispatch(hideLoadingSpinner());
    if (response.success) {
        dispatch(initialArticleSearchResultReceived(response.result));
    } else {
        dispatch(enqueueSnackbar(PoolSnackbar.fatal()));
    }
};

export const fetchArticleDetails = (id: number) => async (dispatch: AppDispatch) => {
    const response = await api.getArticleDetails(id);
    if (response.success) {
        dispatch(articleDetailsLoaded(response.result));
    } else {
        dispatch(notFoundError());
    }
};
